// «Pedir algo a Santi» (propuesta §5.4, P3): la columna 2 pasa a una conversación nueva vacía, no a un formulario
// aparte. Arriba, «Una cosa nueva con Santi»; abajo, una caja grande con el título (opcional: si va vacío lo pone la
// oficina), lo que necesitas y «Para cuándo» (Sin fecha · Mañana · El viernes · Otro día…). Al enviar, la misma pantalla
// pasa a ser la del contrato nuevo (en «Esperando a Santi»). Enter envía desde el texto y desde el título salta al texto;
// enviar sin texto lleva el foco al texto, sin error. Lo escrito se guarda en el navegador como borrador de «pedir».
// El envío lleva su clave: si no se sabe si llegó (5xx, red), el reintento repite la MISMA y el centro no abre dos.
// Las cajas no dejan pasar de lo que acepta el servidor (título 120, texto 4000: LIMITES) y avisan cerca del límite; si
// aun así sobra algo (un borrador de antes) o el servidor dice que no, el aviso dice qué, en palabras.
// Entrega 2 (§5.4): un agente puede dejar un pedido preparado («Te he preparado un pedido para Santi · Revisar» en su
// chat, `GET /agentes/:id`). «Revisar» abre esto con él: con la caja vacía cae en ella (título, texto y fecha) con
// «Borrador de tu agente · <porqué>» y «Descartar»; si Pau ya había escrito algo, no se pisa («… · Verla»). Al enviarlo,
// va con `borrador: <agente>` y el servidor lo usa y lo borra.
// 04-11 (ADR D-037): la misma caja que la de escribir (CAJA), «Para cuándo» con las pastillas de la oficina (ui
// `Pastillas`: gris; la elegida, en blanco) y «Enviar» en cápsula de oro.
import { Bot, Calendar, Check, Loader2, SendHorizontal } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { t } from '../../../i18n';
import { shouldSendOnEnter } from '../../../lib/composer-keys';
import { cn } from '../../../lib/utils';
import { MD_QUERY, useMediaQuery } from '../../../hooks/useMediaQuery';
import { guardarBorrador, useBorrador } from '../chat/borradores';
import { envioTermino } from '../errores';
import { Boton } from '../ui';
import { LIMITES, pelotasApi } from './api';
import { useBorradorEnCaja } from './borradorAgente';
import { CAJA, CAJA_BORRADOR, CAJA_NORMAL, CabeceraBorrador, datosInvalidos, errorDeEnvio, FANTASMA_GRIS } from './CompositorSanti';
import { CabeceraSanti, MarcoColumna } from './MarcoSanti';
import { AvatarPersona, Contador, ENLACE } from './piezas';
import { pedirHilo, usePelotasStore } from './store';

const B_TITULO = 'santi:pedir:titulo';
const B_TEXTO = 'santi:pedir';
const B_FECHA = 'santi:pedir:fecha';

const DIA_SEMANA = new Intl.DateTimeFormat('es-ES', { weekday: 'long' });
const MES_CORTO = new Intl.DateTimeFormat('es-ES', { month: 'short' });

/** `AAAA-MM-DD` de una fecha local. */
export function diaIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** El viernes que viene (hoy, si es lunes a jueves; el de la semana siguiente, si es viernes, sábado o domingo). */
export function proximoViernes(ahora = Date.now()): Date {
  const d = new Date(ahora);
  const faltan = (5 - d.getDay() + 7) % 7 || 7;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + faltan, 12);
}

/** «Viernes 7 nov»: lo que enseña la pastilla elegida. */
export function etiquetaFecha(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  const dia = DIA_SEMANA.format(d);
  return `${dia.charAt(0).toUpperCase()}${dia.slice(1)} ${d.getDate()} ${MES_CORTO.format(d)}`;
}

/** Lo que va en el borrador de la fecha: '' (sin fecha), 'manana', 'viernes' u otro día (`AAAA-MM-DD`). */
export function fechaLimiteDe(eleccion: string, ahora = Date.now()): string | undefined {
  if (!eleccion) return undefined;
  const h = new Date(ahora);
  if (eleccion === 'manana') return diaIso(new Date(h.getFullYear(), h.getMonth(), h.getDate() + 1, 12));
  if (eleccion === 'viernes') return diaIso(proximoViernes(ahora));
  return /^\d{4}-\d{2}-\d{2}$/.test(eleccion) ? eleccion : undefined;
}

/** Qué sobra de lo escrito (o null si cabe): el título primero, que es lo que se ve. */
interface Pedido {
  titulo: string;
  texto: string;
  /** Lo del borrador de la fecha: '' (sin fecha), 'manana', 'viernes' u otro día (`AAAA-MM-DD`). */
  fecha: string;
}
const vacioPedido = (v: Pedido) => !v.titulo.trim() && !v.texto.trim();
const igualPedido = (a: Pedido, b: Pedido) => a.titulo === b.titulo && a.texto === b.texto && a.fecha === b.fecha;

export function sobraEnPedido(titulo: string, texto: string): { campo: 'titulo' | 'texto'; mensaje: string } | null {
  if (titulo.trim().length > LIMITES.titulo) return { campo: 'titulo', mensaje: t('oficinaV2.pelotas.pedir.tituloLargo', { max: LIMITES.titulo }) };
  if (texto.trim().length > LIMITES.texto) return { campo: 'texto', mensaje: t('oficinaV2.pelotas.pedir.textoLargo', { max: LIMITES.texto }) };
  return null;
}

export function PedirAlgo({
  ancha,
  cajon,
  cajonAbierto,
  cajonEncima,
  linea,
  alCajon,
  alCerrar,
  alVerLista,
  alPedido,
}: {
  ancha: boolean;
  cajon: ReactNode;
  cajonAbierto: boolean;
  cajonEncima: boolean;
  linea: ReactNode;
  alCajon: () => void;
  alCerrar: () => void;
  alVerLista?: () => void;
  /** El contrato nuevo ya existe: la ventana lo abre. */
  alPedido: (temaId: number) => void;
}) {
  const [titulo, setTitulo] = useBorrador(B_TITULO);
  const [texto, setTexto] = useBorrador(B_TEXTO);
  const [fecha, setFecha] = useBorrador(B_FECHA);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clave = useRef<string | null>(null);
  const campoTexto = useRef<HTMLTextAreaElement>(null);
  const campoTitulo = useRef<HTMLInputElement>(null);
  const calendario = useRef<HTMLInputElement>(null);
  const dedo = useMediaQuery('(pointer: coarse)');
  const anchoEscritorio = useMediaQuery(MD_QUERY);
  const enterSaltaLinea = dedo && !anchoEscritorio;
  const agentePedido = usePelotasStore((s) => s.pedidoAbierto);
  const pedido = usePelotasStore((s) => (s.pedidoAbierto ? (s.pedidos[s.pedidoAbierto] ?? null) : null));
  const descartarPedido = usePelotasStore((s) => s.descartarPedido);
  useEffect(() => {
    if (agentePedido && usePelotasStore.getState().pedidos[agentePedido] === undefined) void usePelotasStore.getState().cargarPedido(agentePedido);
  }, [agentePedido]);
  const poner = useCallback(
    (v: Pedido) => {
      setTitulo(v.titulo);
      setTexto(v.texto);
      setFecha(v.fecha);
    },
    [setTitulo, setTexto, setFecha],
  );
  const caja = useBorradorEnCaja<Pedido>({
    clave: B_TEXTO,
    borrador: pedido ? { en: pedido.en, valor: { titulo: pedido.titulo, texto: pedido.texto, fecha: pedido.fechaLimite ?? '' }, porque: pedido.porque } : null,
    actual: { titulo, texto, fecha },
    poner,
    vacio: vacioPedido,
    igual: igualPedido,
    alVaciar: useCallback(() => {
      if (agentePedido) descartarPedido(agentePedido);
    }, [agentePedido, descartarPedido]),
  });

  useEffect(() => {
    (titulo ? campoTexto.current : campoTitulo.current)?.focus();
    // Solo al abrir «Pedir algo».
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const el = campoTexto.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(Math.max(el.scrollHeight, 96), 200)}px`;
  }, [texto]);

  const viernes = diaIso(proximoViernes());
  const otroDia = /^\d{4}-\d{2}-\d{2}$/.test(fecha) ? fecha : '';

  async function enviar() {
    if (!texto.trim()) {
      campoTexto.current?.focus();
      return;
    }
    if (enviando) return;
    const sobra = sobraEnPedido(titulo, texto);
    if (sobra) {
      setError(sobra.mensaje);
      (sobra.campo === 'titulo' ? campoTitulo.current : campoTexto.current)?.focus();
      return;
    }
    setEnviando(true);
    setError(null);
    clave.current ??= crypto.randomUUID();
    try {
      const fechaLimite = fechaLimiteDe(fecha);
      // Si sale el pedido que preparó un agente (o lo que Pau hizo con él), va con su agente: el servidor lo usa y lo borra.
      const deAgente = caja.enBorrador && agentePedido ? { borrador: agentePedido } : {};
      const r = await pelotasApi.pedir({ ...(titulo.trim() ? { titulo: titulo.trim() } : {}), texto: texto.trim(), ...(fechaLimite ? { fechaLimite } : {}), ...deAgente }, clave.current);
      clave.current = null;
      caja.soltar();
      if (agentePedido && caja.enBorrador) descartarPedido(agentePedido, false);
      descartar();
      void usePelotasStore.getState().cargarLista();
      pedirHilo(r.temaId);
      alPedido(r.temaId);
    } catch (e) {
      if (envioTermino(e)) clave.current = null;
      // El servidor no aceptó los datos: qué sobra, si se sabe; si no, los límites en palabras.
      setError(datosInvalidos(e) ? (sobraEnPedido(titulo, texto)?.mensaje ?? t('oficinaV2.pelotas.pedir.datosInvalidos', { titulo: LIMITES.titulo, texto: LIMITES.texto })) : errorDeEnvio(e));
    } finally {
      setEnviando(false);
    }
  }

  /** «Descartar» el pedido del agente: fuera de la caja y del servidor. */
  function descartarDelAgente() {
    caja.soltar();
    descartar();
    if (agentePedido) descartarPedido(agentePedido);
    campoTitulo.current?.focus();
  }

  function descartar() {
    setTitulo('');
    setTexto('');
    setFecha('');
    guardarBorrador(B_TITULO, '');
    guardarBorrador(B_TEXTO, '');
    guardarBorrador(B_FECHA, '');
    clave.current = null;
    setError(null);
  }

  function abrirCalendario() {
    const el = calendario.current;
    if (!el) return;
    try {
      if (typeof el.showPicker === 'function') {
        el.showPicker();
        return;
      }
    } catch {
      // Sin permiso para abrirlo así (p. ej. dentro de un iframe): se enfoca y el navegador lo abre a su manera.
    }
    el.focus();
    el.click();
  }

  // Las pastillas de `ui` (Pastillas), a mano porque «Otro día…» abre el calendario del sistema anclado bajo ella.
  const pastilla = (activa: boolean) =>
    cn(
      'inline-flex h-8 shrink-0 items-center gap-1 rounded-full px-3.5 text-o-sub font-medium outline-none transition-colors duration-150 ease-o-apple focus-visible:ring-2 focus-visible:ring-white/85 focus-visible:ring-offset-2 focus-visible:ring-offset-oficina-fondo',
      activa ? 'bg-oficina-texto font-semibold text-oficina-fondo' : 'bg-oficina-control text-oficina-texto2 hover:bg-oficina-pulsado hover:text-oficina-texto',
    );
  const marca = <Check className="h-3.5 w-3.5" strokeWidth={2.75} aria-hidden />;

  return (
    <MarcoColumna
      cabecera={<CabeceraSanti titulo={t('oficinaV2.pelotas.pedir.titulo')} sub={t('oficinaV2.pelotas.pedir.sub')} ancha={ancha} cajonAbierto={cajonAbierto} alCajon={alCajon} alCerrar={alCerrar} alVerLista={alVerLista} />}
      cuerpo={
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 overflow-y-auto p-8 text-center" data-testid="pedir-vacio">
          <AvatarPersona persona="santi" tamano={40} className="mb-2" />
          <p className="text-o-titulo3 font-semibold text-oficina-texto">{t('oficinaV2.pelotas.pedir.vacioTitulo')}</p>
          <p className="max-w-[440px] text-o-cuerpo leading-relaxed text-oficina-texto2">{t('oficinaV2.pelotas.pedir.vacioTexto')}</p>
        </div>
      }
      pie={
        <footer className="shrink-0 bg-oficina-elevada px-6 pb-4 pt-2" data-testid="pedir-algo-caja">
          {error ? (
            <p role="alert" className="mb-2 px-1 text-o-sub text-oficina-error">
              {error}
            </p>
          ) : null}
          {caja.hayOtro ? (
            <p className="mb-2 flex items-center gap-1.5 px-1 text-o-sub text-oficina-texto2" data-testid="borrador-hay-otro">
              <Bot className="h-4 w-4 shrink-0 text-oficina-meta-pau" aria-hidden />
              {t('oficinaV2.pelotas.borrador.hayUno')} ·
              <button type="button" onClick={caja.verla} className={ENLACE} data-testid="borrador-verla">
                {t('oficinaV2.pelotas.borrador.verla')}
              </button>
            </p>
          ) : caja.deshacer ? (
            <p className="mb-2 flex items-center gap-1.5 px-1 text-o-sub text-oficina-texto2" data-testid="borrador-puesto">
              {t('oficinaV2.pelotas.borrador.puesto')} ·
              <button type="button" onClick={caja.deshacer} className={ENLACE} data-testid="borrador-deshacer">
                {t('oficinaV2.pelotas.borrador.deshacer')}
              </button>
            </p>
          ) : null}
          <div
            className={cn(CAJA, caja.enBorrador ? CAJA_BORRADOR : CAJA_NORMAL)}
            data-testid="pedir-caja"
            data-borrador={caja.enBorrador ? 'true' : undefined}
          >
            {caja.enBorrador ? <CabeceraBorrador porque={caja.porque} alDescartar={descartarDelAgente} /> : null}
            <div className="flex items-center gap-3 border-b border-white/10 pr-4">
              <input
                ref={campoTitulo}
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    campoTexto.current?.focus();
                  }
                }}
                maxLength={LIMITES.titulo}
                placeholder={t('oficinaV2.pelotas.pedir.campoTitulo')}
                aria-label={t('oficinaV2.pelotas.pedir.campoTitulo')}
                className="block min-w-0 flex-1 bg-transparent pb-3 pl-4 pt-3.5 text-o-titulo2 font-semibold text-oficina-texto outline-none placeholder:font-medium placeholder:text-oficina-texto3"
                data-testid="pedir-titulo"
              />
              <Contador n={titulo.length} max={LIMITES.titulo} />
            </div>
            <textarea
              ref={campoTexto}
              rows={3}
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              onKeyDown={(e) => {
                if (!e.nativeEvent.isComposing && shouldSendOnEnter(e, { isCoarsePointer: enterSaltaLinea })) {
                  e.preventDefault();
                  void enviar();
                }
              }}
              maxLength={LIMITES.texto}
              placeholder={t('oficinaV2.pelotas.pedir.campoTexto')}
              aria-label={t('oficinaV2.pelotas.pedir.campoTexto')}
              className="block max-h-[200px] min-h-24 w-full resize-none bg-transparent px-4 pb-1 pt-3.5 text-o-cuerpo text-oficina-texto outline-none placeholder:text-oficina-texto3"
              data-testid="pedir-texto"
            />
            <div role="radiogroup" aria-label={t('oficinaV2.pelotas.pedir.paraCuando')} className="flex flex-wrap items-center gap-2 px-4 pb-1 pt-2" data-testid="pedir-fecha">
              <span className="mr-1 inline-flex items-center gap-2 text-o-sub font-medium text-oficina-texto2">
                <Calendar className="h-4 w-4" aria-hidden />
                {t('oficinaV2.pelotas.pedir.paraCuando')}
              </span>
              <button type="button" role="radio" aria-checked={!fecha} onClick={() => setFecha('')} className={pastilla(!fecha)}>
                {!fecha ? marca : null}
                {t('oficinaV2.pelotas.pedir.sinFecha')}
              </button>
              <button type="button" role="radio" aria-checked={fecha === 'manana'} onClick={() => setFecha('manana')} className={pastilla(fecha === 'manana')}>
                {fecha === 'manana' ? marca : null}
                {t('oficinaV2.pelotas.pedir.manana')}
              </button>
              <button type="button" role="radio" aria-checked={fecha === 'viernes'} onClick={() => setFecha('viernes')} className={pastilla(fecha === 'viernes')}>
                {fecha === 'viernes' ? marca : null}
                {fecha === 'viernes' ? etiquetaFecha(viernes) : t('oficinaV2.pelotas.pedir.elViernes')}
              </button>
              <span className="relative inline-flex">
                <button type="button" role="radio" aria-checked={!!otroDia} onClick={abrirCalendario} className={pastilla(!!otroDia)} data-testid="pedir-otro-dia">
                  {otroDia ? marca : null}
                  {otroDia ? etiquetaFecha(otroDia) : t('oficinaV2.pelotas.pedir.otroDia')}
                </button>
                {/* El calendario del sistema (el del iPad, el del navegador), anclado bajo la pastilla. */}
                <input
                  ref={calendario}
                  type="date"
                  tabIndex={-1}
                  aria-hidden
                  min={diaIso(new Date())}
                  value={otroDia}
                  onChange={(e) => e.target.value && setFecha(e.target.value)}
                  className="pointer-events-none absolute bottom-0 left-0 h-px w-full opacity-0"
                  data-testid="pedir-calendario"
                />
              </span>
            </div>
            <div className="flex items-center gap-2 px-1.5 pb-1.5 pt-1">
              <span className="flex-1 pl-2.5">
                <Contador n={texto.length} max={LIMITES.texto} />
              </span>
              {/* Con el pedido del agente en la caja, su «Descartar» va arriba (el de la caja con borrador); aquí no se repite. */}
              {caja.enBorrador ? null : (
                <Boton variante="fantasma" onClick={descartar} className={FANTASMA_GRIS} data-testid="pedir-descartar">
                  {t('oficinaV2.pelotas.pedir.descartar')}
                </Boton>
              )}
              <Boton variante="primario" aria-disabled={!texto.trim()} onClick={() => void enviar()} data-testid="pedir-enviar">
                {enviando ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <SendHorizontal className="h-4 w-4" strokeWidth={2.2} aria-hidden />}
                {t('oficinaV2.pelotas.chat.enviar')}
              </Boton>
            </div>
          </div>
        </footer>
      }
      linea={linea}
      cajon={cajon}
      cajonEncima={cajonEncima}
    />
  );
}
